import { useNavigate } from "react-router-dom";
import { Zap, Sparkles, Hammer, Check, ArrowLeft } from "lucide-react";

// Per-builder hero card: only the mode you're in, with hype features + a 3-step "get started".
// Colours match the Plans page (Generated blue · Creator sage · Social red).
const MODES = {
  diy: {
    Icon: Hammer, color: "#5d8a52", title: "Video Creator Mode", price: "Building is FREE · $2.99 to export",
    tag: "Upload your story — Smart-Edit handles the editing. No timeline, no editing skills needed.",
    features: ["Smart-Edit built in — we cut, time & polish", "Your photos & 10-second video clips", "Your story, in your own words", "Record your voice with a 3-2-1 countdown", "Auto captions in your colours & fonts", "Trim, swap & re-record any clip"],
    steps: ["Give your video a title", "Add a photo or clip for each step", "Write or record each step — then tap Make My Video"],
  },
  generate: {
    Icon: Sparkles, color: "#1e3aaa", title: "Video Generate Mode", price: "$3.99 · everything included",
    tag: "Type your idea — we write, illustrate, voice and Smart-Edit the whole video for you.",
    features: ["Smart-Edit built in — zero editing", "AI script + 15 illustrated steps", "Studio-quality voice-over", "PDF, slideshow, quiz & worksheet", "Chat with an AI Expert add-on", "Amazon affiliate links built in"],
    steps: ["Type your idea below", "Pick your style, level & voice", "Tap Generate — we build the rest"],
  },
  social: {
    Icon: Zap, color: "#e0455c", title: "Social Video Mode", price: "$1.99 · one 60-second reel",
    tag: "Drop in your clips — Smart-Edit syncs, captions and cuts your reel. Made for Shorts, Reels & TikTok.",
    features: ["Smart-Edit built in — no editing needed", "True 9:16 portrait reel", "Up to 6 video clips or 12 photos", "Voice-over + your own music", "On-screen text & AI caption helpers", "Ready in under a minute"],
    steps: ["Pick your clip mix", "Add your clips or photos", "Add text, music & voice — then render"],
  },
};

export default function CreatorModeCards({ active = "diy" }) {
  const navigate = useNavigate();
  const m = MODES[active] || MODES.diy;
  const back = () => navigate("/diy");
  return (
    <div className="sms-tweed relative rounded-2xl border-2 p-5 sm:p-6 mb-5 sms-fade-up" style={{ borderColor: m.color }} data-testid={`mode-hero-${active}`}>
      {active !== "diy" && (
        <button type="button" onClick={back} data-testid="mode-hero-switch"
          className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] font-bold text-white/60 hover:text-white transition-colors">
          <ArrowLeft size={14} /> Creator Mode
        </button>
      )}
      <div className="flex items-center gap-3 pr-20">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: m.color }}><m.Icon size={22} className="text-white" /></span>
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-extrabold text-white leading-tight">{m.title}</h2>
          <div className="text-xs sm:text-sm font-extrabold" style={{ color: active === "generate" ? "#8ea2ff" : active === "social" ? "#ff8a9a" : "#ffd21e" }} data-testid="mode-hero-price">{m.price}</div>
        </div>
      </div>
      <p className="text-sm text-white/75 mt-3">{m.tag}</p>
      <div className="grid sm:grid-cols-[1.4fr_1fr] gap-4 mt-4">
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5" data-testid="mode-hero-features">
          {m.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm font-semibold text-white"><Check size={16} className="mt-0.5 shrink-0" style={{ color: m.color === "#1e3aaa" ? "#8ea2ff" : m.color }} />{f}</li>
          ))}
        </ul>
        <div className="rounded-xl bg-black/50 border border-white/10 p-3" data-testid="mode-hero-steps">
          <div className="text-[11px] font-extrabold tracking-widest text-white/60 mb-2">GET STARTED</div>
          <ol className="space-y-1.5">
            {m.steps.map((s, i) => (
              <li key={s} className="flex items-start gap-2 text-sm text-white">
                <span className="w-5 h-5 rounded-full text-[11px] font-extrabold flex items-center justify-center shrink-0 text-white" style={{ background: m.color }}>{i + 1}</span>{s}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
